/**
 * One canonical spelling for a hashtag URL segment.
 *
 * The backend matches tags case-insensitively, so `/hashtag/Love`,
 * `/hashtag/LOVE` and `/hashtag/love` all returned HTTP 200 with the same 20
 * packs — and each declared itself canonical at its own casing. Every inbound
 * link or typed URL with different capitalisation therefore minted another
 * indexable duplicate of a page that already exists.
 *
 * The rule deliberately mirrors `hashtagsFrom()` in
 * scripts/generate-sitemaps.mjs, which lowercases every tag before writing it
 * to the sitemap. Matching it exactly is the point: if hrefs and canonicals
 * disagreed with the sitemap the site would advertise one spelling and link
 * another, which is a worse bug than the one being fixed. Any change here has
 * to be made in both places — `tests/hashtag-slug.test.ts` asserts they agree.
 */
export function hashtagSlug(tag: string): string {
  return tag.trim().toLowerCase();
}

import { listingPagePath } from "@/lib/seo/site";

/** The canonical pathname for a hashtag listing, ready for `pageMetadata`. */
export function hashtagPath(tag: string, page?: number): string {
  // One builder for every listing's page segment — the Pager on the same page
  // uses it too, so the canonical and the pager links cannot drift apart.
  return listingPagePath(`/hashtag/${encodeURIComponent(hashtagSlug(tag))}`, page ?? 1);
}

/**
 * The tag a `/hashtag/[hashtag]` segment stands for, safely.
 *
 * Next hands the segment over still percent-encoded and `fetchHashtagFeed`
 * encodes its argument again, so an undecoded param goes out double-encoded.
 * But a bare `decodeURIComponent` is not safe on a URL anyone can type:
 * `/hashtag/%25` decodes to "%", and `decodeURIComponent("%")` throws
 * `URIError`, which escaped `generateMetadata` as an uncatchable 500. A 500 on a
 * listing makes Google back off crawling the whole section.
 *
 * Mirrors `decodeSlug` in app/sticker-pack/[slug]/page.tsx: decode when we can,
 * otherwise pass the raw segment through and let the route 404 it.
 */
export function decodeTag(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}
